import type { Track } from '../../api.js';
import { moduleOf } from './moduleMap.js';
import { attentionStateFor, boardReviewFlags } from './statusMap.js';
import TrackIdBadge from './TrackIdBadge.js';

const AGE_WARN_THRESHOLD_MS = 60 * 60 * 1000;

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function BoardCard({
  track,
  isWarnColumn,
  onOpen,
}: {
  track: Track;
  isWarnColumn: boolean;
  onOpen: (id: string) => void;
}) {
  const chips = boardReviewFlags(track);
  const attention = attentionStateFor(track);
  const hasBlock = chips.some((c) => c.verdict === 'block');
  const hasFlag = chips.some((c) => c.verdict === 'flag');
  const cardState =
    hasBlock || attention?.kind === 'halted'
      ? 'block'
      : hasFlag || attention?.kind === 'needs_input' || isWarnColumn
        ? 'warn'
        : 'normal';
  const repoLabel = track.spec.target_repo?.split('/')[1] ?? 'unknown';
  const origin = (track as Track & { origin?: string | null }).origin;
  const ageMs = Math.max(0, Date.now() - new Date(track.updated_at).getTime());

  return (
    <button type="button" className="board-card" data-card-state={cardState} onClick={() => onOpen(track.id)}>
      <div className="board-card-title">{track.title}</div>
      <div className="board-card-chips">
        <span className="board-chip board-chip-module">{moduleOf(track)}</span>
        <span className="board-chip board-chip-repo">{repoLabel}</span>
        {attention?.kind === 'halted' && (
          <span
            className="board-chip board-chip-verdict"
            data-verdict="block"
            title={attention.reason ?? undefined}
          >
            ⛔ Halted
          </span>
        )}
        {attention?.kind === 'needs_input' && (
          <span
            className="board-chip board-chip-verdict"
            data-verdict="flag"
            title={attention.reason ?? undefined}
          >
            ⏸ Needs input
          </span>
        )}
        {chips.map((c) => (
          <span key={c.key} className="board-chip board-chip-verdict" data-verdict={c.verdict}>
            {c.label}
          </span>
        ))}
      </div>
      <div className="board-card-footer">
        <TrackIdBadge id={track.id} className="board-card-id" />
        {origin && <span className="board-card-origin">💬 {origin}</span>}
        <span className={`board-card-age${ageMs > AGE_WARN_THRESHOLD_MS ? ' board-card-age-warn' : ''}`}>
          {relativeTime(track.updated_at)}
        </span>
      </div>
    </button>
  );
}
